<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  checkFileSizeLimit,
  truncateFileName,
} from 'shared/helpers/FileHelper';
import { WHATSAPP_MEDIA_LIMITS } from 'shared/constants/campaign';

const props = defineProps({
  mediaFile: {
    type: File,
    default: null,
  },
  mediaFileName: {
    type: String,
    default: '',
  },
  mediaHeaderFormat: {
    type: String,
    default: null,
  },
  totalSteps: {
    type: Number,
    required: true,
  },
  currentStep: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits([
  'mediaFileSelected',
  'removeMediaFile',
  'back',
  'next',
]);

const { t } = useI18n();
const mediaFileInput = ref(null);

const validateMediaFile = (file, headerFormat) => {
  const validation = WHATSAPP_MEDIA_LIMITS[headerFormat];
  if (!validation.types.includes(file.type)) {
    return { valid: false, error: 'TYPE_ERROR' };
  }
  if (!checkFileSizeLimit(file, validation.maxSizeMB)) {
    return { valid: false, error: 'SIZE_ERROR' };
  }
  return { valid: true };
};

const handleMediaFileClick = () => mediaFileInput.value?.click();

const handleMediaFileChange = async () => {
  const file = mediaFileInput.value?.files[0];
  if (file) {
    const validation = validateMediaFile(file, props.mediaHeaderFormat);
    if (!validation.valid) {
      const errorKey = `CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.MEDIA.${validation.error}`;
      useAlert(t(errorKey));
      return;
    }

    try {
      const fileName = truncateFileName(file.name);
      emit('mediaFileSelected', { file, fileName });

      useAlert(t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.MEDIA.UPLOAD_SUCCESS'));
    } catch (error) {
      useAlert(t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.MEDIA.UPLOAD_ERROR'));
    }
  }
};

const handleRemoveMediaFile = () => {
  emit('removeMediaFile');
  if (mediaFileInput.value) {
    mediaFileInput.value.value = null;
  }
};

const getAcceptedFileTypes = format => {
  const typeMap = {
    IMAGE: '.jpg,.jpeg,.png',
    VIDEO: '.mp4,.3gp',
    DOCUMENT: '.pdf',
  };
  return typeMap[format] || '';
};

const getMediaDescription = format => {
  const descriptionMap = {
    IMAGE: 'CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.MEDIA.DESCRIPTION_IMAGE',
    VIDEO: 'CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.MEDIA.DESCRIPTION_VIDEO',
    DOCUMENT: 'CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.MEDIA.DESCRIPTION_DOCUMENT',
  };
  return descriptionMap[format] || '';
};
</script>

<template>
  <div>
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-lg font-medium">
        {{ t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.STEPS.MEDIA_UPLOAD') }}
      </h3>
      <div class="flex items-center gap-2">
        <div class="text-xs text-n-slate-10">
          {{
            t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.STEP', {
              current: currentStep + 1,
              total: totalSteps,
            })
          }}
        </div>
      </div>
    </div>

    <div
      class="flex flex-col gap-2 p-4 border border-dashed rounded-md border-n-slate-6"
    >
      <div class="flex items-center justify-between">
        <span class="text-sm text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.MEDIA.LABEL') }}
        </span>
        <div class="flex items-center gap-2">
          <span v-if="mediaFile" class="text-sm text-n-slate-12">
            {{ mediaFileName }}
          </span>
          <Button
            v-if="!mediaFile"
            :label="t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.MEDIA.CHOOSE_FILE')"
            icon="i-lucide-upload"
            color="slate"
            variant="ghost"
            size="sm"
            class="!w-fit"
            @click="handleMediaFileClick"
          />
          <div v-else class="flex items-center gap-1">
            <Button
              :label="t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.MEDIA.CHANGE')"
              color="slate"
              variant="ghost"
              size="sm"
              @click="handleMediaFileClick"
            />
            <div class="w-px h-3 bg-n-strong" />
            <Button
              icon="i-lucide-trash"
              color="slate"
              variant="ghost"
              size="sm"
              @click="handleRemoveMediaFile"
            />
          </div>
        </div>
      </div>

      <p class="text-xs text-n-slate-10">
        {{ t(getMediaDescription(mediaHeaderFormat)) }}
      </p>
    </div>

    <div class="flex justify-between mt-4">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.BUTTONS.BACK')"
        @click="emit('back')"
      />
      <Button
        variant="solid"
        color="primary"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP_CSV.CREATE.FORM.BUTTONS.NEXT')"
        :disabled="!mediaFile"
        @click="emit('next')"
      />
    </div>

    <input
      ref="mediaFileInput"
      type="file"
      :accept="getAcceptedFileTypes(mediaHeaderFormat)"
      class="hidden"
      @change="handleMediaFileChange"
    />
  </div>
</template>
